import { lazy, Suspense, useMemo, useState } from "react";
import { ErrorBoundary, type NonBlockEntrypoint } from "@softr-io/blocks-sdk/react";
import type { BlockDefinition, LegacyBlockTheme } from "@softr-io/blocks-sdk";
import { useAppConstants } from "../AppConstantsContext";
import { retry } from "../retry";
import { usePageContext } from "@/PageContext";

const SESSION_KEY = "softr_backup_code_used";

interface Props {
    theme: LegacyBlockTheme;
    definitions: ReadonlyArray<BlockDefinition>;
}

export function MfaLostAccessPromptController(props: Props) {
    // Lazy initializer runs once, on mount, and the decision sticks across re-renders —
    // MfaLostAccessPromptUI deletes this flag ~500ms after mount (right before showing the
    // toast), so re-checking sessionStorage on every render would flip this back to `false`
    // on the next unrelated re-render (page-renderer re-renders often early in a page's life —
    // navigation progress, block-readiness polling), unmounting the toast moments after it appears.
    const [shouldLoad] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");
    if (!shouldLoad) {
        return null;
    }
    return <MfaLostAccessPromptControllerImpl {...props} />;
}

function MfaLostAccessPromptControllerImpl({ theme, definitions }: Props) {
    const { appId, baseUrl, spaMode } = useAppConstants();
    const { pageId } = usePageContext();
    const baseUrlWithoutBlocks = baseUrl.replace(/\/blocks(\/)?$/, "");
    const mfaLostAccessPromptUIPath = `${baseUrlWithoutBlocks}/assets/MfaLostAccessPromptUI.mjs`;

    const MfaLostAccessPromptUI = useMemo(
        () =>
            lazy(() =>
                retry(
                    () =>
                        import(
                            /* webpackIgnore: true */
                            mfaLostAccessPromptUIPath
                        ) as Promise<{ default: NonBlockEntrypoint }>,
                ),
            ),
        [mfaLostAccessPromptUIPath],
    );

    return (
        <Suspense fallback={null}>
            <ErrorBoundary>
                <MfaLostAccessPromptUI
                    theme={theme}
                    appId={appId}
                    pageId={pageId}
                    spaMode={spaMode}
                    baseUrlForAssetsDir={baseUrlWithoutBlocks}
                    blocks={definitions}
                />
            </ErrorBoundary>
        </Suspense>
    );
}
